// modules
import React from 'react'

import PropTypes from 'prop-types'

// utils
import { catchError } from '@emmacare/common/api/httpClient'
import { addReactError } from '@emmacare/common/services/datadogRum'

import OfflineError from 'src/components/OfflineError'
// components
import UnexpectedError from 'src/components/UnexpectedError'

class ErrorBoundary extends React.PureComponent {
  static propTypes = {
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired
    }).isRequired,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]).isRequired,
    errorFallback: PropTypes.node,
    noInternetConnection: PropTypes.bool.isRequired,
    setNoInternetConnection: PropTypes.func.isRequired
  }

  static defaultProps = {
    errorFallback: null
  }

  constructor(props) {
    super(props)

    this.state = {
      hasError: false,
      path: props?.location?.pathname || '',
      isOnline: true,
      requestError: false,
      isReloadRequired: false
    }
  }

  componentDidMount = () => {
    this.handleConnectionChange()
    window.addEventListener('online', this.handleConnectionChange)
    window.addEventListener('offline', this.handleConnectionChange)
  }

  componentWillUnmount = () => {
    window.removeEventListener('online', this.handleConnectionChange)
    window.removeEventListener('offline', this.handleConnectionChange)
  }

  componentDidUpdate = (_, prevState) => {
    const { noInternetConnection, setNoInternetConnection } = this.props
    const { isOnline } = this.state

    if (isOnline && !prevState.isOnline) return

    if (noInternetConnection && isOnline) {
      catchError(
        {},
        {
          onOk: () => {
            setNoInternetConnection(false)
          }
        }
      )
    }
  }

  handleConnectionChange = () => {
    const { setNoInternetConnection } = this.props

    this.setState({
      isOnline: navigator.onLine
    })

    if (navigator.onLine) {
      setNoInternetConnection(false)
    }
  }

  componentDidCatch = (error, errorInfo) => {
    addReactError(error, errorInfo)
  }

  static getDerivedStateFromError = (err = null) => ({
    hasError: true,
    requestError: err.name === 'ChunkLoadError'
  })

  static getDerivedStateFromProps(props = null, state = null) {
    const { path } = state
    const propsPathname = props?.location?.pathname | ''

    if (propsPathname !== path) {
      return {
        hasError: false,
        path: propsPathname
      }
    }

    return null
  }

  render = () => {
    const { hasError, isOnline, requestError, isReloadRequired } = this.state
    const { children, errorFallback, noInternetConnection } = this.props

    if (
      (!isOnline && (requestError || noInternetConnection)) ||
      isReloadRequired
    ) {
      return <OfflineError />
    }

    if (hasError) {
      return errorFallback || <UnexpectedError />
    }

    return children
  }
}

export default ErrorBoundary
